import { FormBuilder } from '@fareye/form-builder'
import { Button, Flex, IRenderControl, toast } from '@fareye/ui'
import {
  cancelToken,
  cloneDeep,
  get,
  isEmpty,
  post,
  put,
  differenceWith,
  isEqual
} from '@fareye/utils'
import React from 'react'
import { HEADER_INFLECTION } from '../../../../config/constants'
import { filterKeyFromObject, getErrorMessage } from '../../../../utility/funcs'
import { IChildUserConfig, ISpecificKey } from '../../../../utility/types'
import { DataListing } from '../../components/DataListing'
interface IChildUserListing extends IChildUserConfig {
  baseUrl: string
}
export function ChildUserListing({
  id,
  label,
  config,
  baseUrl
}: IChildUserListing) {
  const [apiData, setApiData] = React.useState<IRenderControl>({
    data: [],
    message: '',
    isError: false,
    isLoading: false,
    isSuccess: true
  })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [selectedDataForEdit, setSelectedDataForEdit] = React.useState<any>({})
  const source = cancelToken().source()
  const crudSource = cancelToken().source()
  const [formSubmitted, setFormSubmitted] = React.useState(false)
  const [formValues, setFormValues] = React.useState<any>({})
  const [edit, setEdit] = React.useState(false)
  const [initialValue, setInitialValue] = React.useState<any>({})
  const { apiConfig, selectedColumns } = config.children['listChildUser']
  const { apiUrl } = apiConfig['listChildUser']
  const { formRenderingConfig, apiConfig: crudConfig } = edit
    ? config.children['editChildUser']
    : config.children['addChildUser']

  React.useEffect(() => {
    fetchData()
    return () => source.cancel('Component unmount in ChildUserListing')
  }, [])

  //NOTE:Invoke when we submit form button.
  React.useEffect(() => {
    if (!isEmpty(formValues.values)) {
      const payload = getFormValue()
      if (edit) {
        updateUser(payload)
      } else {
        createUser(payload)
      }
    }
    return () => crudSource.cancel()
  }, [formValues.values])

  //NOTE:Invoke when edit button will be clicked in listing.
  React.useEffect(() => {
    if (!isEmpty(selectedDataForEdit)) {
      setEdit(true)
      const {
        firstName,
        lastName,
        email,
        state,
        username,
        primaryPhoneNo,
        roles,
        configFilters,
        userTimezone
      } = selectedDataForEdit
      const clonedInitialValue = {
            firstName,
            lastName,
            email,
            state: { label: state, value: state },
            username,
            primaryPhoneNo,
            roleIds: roles,
            configFilterIds: configFilters,
            userTimezone: { label: userTimezone, value: userTimezone }
      }
      setInitialValue(clonedInitialValue)
    }
  }, [selectedDataForEdit])

  //NOTE: Fetching Child Users
  async function fetchData() {
    try {
      setApiData({ ...apiData, isLoading: true })
      const { data } = await get(apiUrl, source.token, {
        headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true }
      })
      setApiData({ ...apiData, isLoading: false, data: data.users })
    } catch (ex:any) {
      setApiData({
        ...apiData,
        isLoading: false,
        isError: true,
        message: 'Something went wrong...'
      })
      console.warn('ChildUserListing', ex)
    }
  }

  //NOTE: Creating child user
  async function createUser(payload: any) {
    try {
      setFormSubmitted(true)
      const resp = await post(
        crudConfig.addChildUser.apiUrl,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }
      formValues.resetForm()
      toast.success('User created successfully.')
      fetchData()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  //NOTE: Updating  child user
  async function updateUser(payload: any) {
    try {
      setFormSubmitted(true)
      const resp = await put(
        `${crudConfig.editChildUser.apiUrl}/${selectedDataForEdit.id}`,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }
      toast.success('User updated successfully.')
      fetchData()
      resetEditForm()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  //NOTE: Getting form Values and making payload for form submission.
  function getFormValue() {
    const clonedFormValue: any = cloneDeep(formValues.values)
    clonedFormValue.roleIds = filterKeyFromObject(clonedFormValue.roleIds, 'id')
    clonedFormValue.configFilterIds = clonedFormValue.configFilterIds
      ? clonedFormValue.configFilterIds.map((item: any) => item.id)
      : null
    clonedFormValue.state = clonedFormValue.state
      ? clonedFormValue.state.value
      : null
    clonedFormValue.userTimezone = clonedFormValue?.userTimezone?.value
    clonedFormValue.language = clonedFormValue?.language?.value
    const payload = {
      user:{
          ...clonedFormValue
      }
    }

    return payload
  }

  function resetEditForm() {
    setInitialValue({})
    setSelectedDataForEdit(null)
    setEdit(false)
  }
  function validateForm(val: any): any {
    return {
      ...(val.email === val.username
        ? { email: '*Enter different Email ID' }
        : {}),
      ...(val.passwordConfirmation !== val.password
        ? { passwordConfirmation: '*Enter Same Password' }
        : {})
    }
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginLeft: 10,
        overflow: 'auto'
      }}
    >
      {edit && (
        <Flex m={4} justifyContentFlexEnd>
          <Button mr={10} variant="primary m" onClick={() => resetEditForm()}>
            Add Child
          </Button>
        </Flex>
      )}
      <Flex style={{ marginBottom: 10 }} width={1} bg="#fff">
        <FormBuilder
          setSubmitValues={setFormValues}
          formDefaultValue={initialValue}
          config={formRenderingConfig}
          formGroupName={id}
          isLoading={formSubmitted}
          baseUrl={baseUrl}
          validate={validateForm}
        />
      </Flex>

      <DataListing
        apiData={apiData}
        label={label}
        containerRef={containerRef}
        selectedColumns={selectedColumns ? selectedColumns : []}
        setSelectedDataForEdit={setSelectedDataForEdit}
      />
    </div>
  )
}
